/**
 * ✅ PRAGMATIC SOLUTION: Suppress Non-Critical React 18 + Ant Design Warnings
 * 
 * These warnings are caused by Ant Design v4.6.6 compatibility with React 18.
 * They don't affect application functionality but clutter the development console.
 * 
 * Warnings suppressed:
 * 1. onPointerEnterCapture/onPointerLeaveCapture - Unknown event handlers in React 18
 * 2. defaultProps deprecation - Will be removed in future React versions
 * 3. findDOMNode deprecation - Legacy DOM access pattern
 */

export const suppressNonCriticalWarnings = () => {
  if (process.env.NODE_ENV !== 'development') {
    return; // Only suppress in development
  }

  // Store original console methods
  const originalWarn = console.warn;
  const originalError = console.error;

  // Warning patterns to suppress (case-insensitive)
  const suppressedWarningPatterns = [
    // React 18 + Ant Design compatibility
    /unknown event handler property.*onpointerentercapture/i,
    /unknown event handler property.*onpointerleavecapture/i,
    
    // React defaultProps deprecation warnings
    /support for defaultprops will be removed from function components/i,
    
    // React findDOMNode deprecation
    /finddomnode is deprecated and will be removed/i,
    
    // Other common non-critical warnings
    /react-dom\.development\.js.*warning/i,
  ];

  // Error patterns that should be shown as warnings instead
  const downgradeErrorPatterns = [
    /warning.*unknown event handler property/i,
  ];

  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    
    const shouldSuppress = suppressedWarningPatterns.some(pattern => 
      pattern.test(message)
    );
    
    if (!shouldSuppress) {
      originalWarn.apply(console, args);
    } else if (process.env.REACT_APP_DEBUG_WARNINGS === 'true') {
      // Allow showing suppressed warnings with debug flag
      originalWarn.apply(console, ['[SUPPRESSED]', ...args]);
    }
  };

  console.error = (...args: any[]) => {
    const message = args.join(' ');
    
    // Check if this error should be downgraded to a warning
    const shouldDowngrade = downgradeErrorPatterns.some(pattern => 
      pattern.test(message)
    );
    
    if (shouldDowngrade) {
      // Downgrade error to warning and potentially suppress
      console.warn(...args);
    } else {
      // Show real errors normally
      originalError.apply(console, args);
    }
  };

  // Log that warning suppression is active
  console.info(
    '%c✅ React Warning Suppression Active', 
    'color: #10B981; font-weight: bold;',
    '\nSuppressing non-critical Ant Design + React 18 compatibility warnings.',
    '\nSet REACT_APP_DEBUG_WARNINGS=true to see suppressed warnings.',
    '\nReal errors and critical warnings will still be shown.'
  );
};

export default suppressNonCriticalWarnings;